import {
  afterNextRender,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  resource,
  viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { toSignal } from '@angular/core/rxjs-interop';
import { HelpSection, helpSections, helpTitle, renderHelp } from './help-markdown';
import { CrownLoaderComponent } from '../crown-loader/crown-loader.component';

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * What escape closes before the help: dialogs and windows (e.g. the search dialog of Crownbase), the popups of lists,
 * pickers, menus and popovers, and the search box (its results, or the search panel on mobile).
 */
const ESCAPE_OWNERS = [
  'kendo-dialog',
  'kendo-window',
  '.k-dialog-wrapper',
  '.k-window',
  '[role="dialog"]',
  '[aria-modal="true"]',
  'dialog[open]',
  '.k-animation-container',
  '.k-popup',
  '.k-popover',
].join(', ');

/** A manual in the list of help topics. */
export interface HelpTopic {
  /** The manual, e.g. `klanten.md`. */
  article: string;
  title: string;
}

/**
 * The manual of the current screen, in a panel at the right that leaves the screen usable. Links between the manuals
 * open in the panel, with a back button; links to elsewhere open in a new tab.
 */
@Component({
  selector: 'rc-help-panel',
  imports: [TranslateModule, CapitalizePipe, CrownLoaderComponent],
  templateUrl: './help-panel.component.html',
  styleUrl: './help-panel.component.scss',
  host: {
    class: 'help-panel',
    role: 'complementary',
    tabindex: '-1',
    '(keydown.escape)': 'closed.emit()',
    '(document:keydown.escape)': 'onDocumentEscape($event)',
  },
})
export class HelpPanelComponent {
  /** Where the manuals and their images are, e.g. `/help/nl`. */
  readonly basePath = input.required<string>();
  /** The manual of the current screen, with an optional anchor (`klanten.md#leveradressen`); none shows the topics. */
  readonly article = input.required<string | null>();
  readonly topics = input<HelpTopic[]>([]);
  readonly closed = output<void>();

  /** What the panel shows: the manual of the screen, until the user follows a link. */
  protected readonly current = linkedSignal(() => this.article());
  protected readonly history = linkedSignal<string | null, (string | null)[]>({
    source: this.article,
    computation: () => [],
  });

  /** The manuals loaded so far, by file: the topics need them all, and going back needs no reload. */
  #manuals = new Map<string, Promise<string>>();
  /** A manual by its url (base path and file), so another language loads its own manuals. */
  #load(url: string): Promise<string> {
    let manual = this.#manuals.get(url);
    if (!manual) {
      manual = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`${response.status} ${url}`);
        return response.text();
      });
      manual.catch(() => this.#manuals.delete(url));
      this.#manuals.set(url, manual);
    }
    return manual;
  }

  readonly #file = computed(() => this.current()?.split('#')[0] || null);
  /** The manual of the current screen, which the topics open. */
  protected readonly screenFile = computed(() => this.article()?.split('#')[0] || null);
  readonly #markdown = resource({
    params: () => (this.#file() ? `${this.basePath()}/${this.#file()}` : undefined),
    loader: ({ params }) => this.#load(params),
  });
  /**
   * The topics with the title (in the language of the manuals) and the sections of their manual, once loaded (until
   * then, with the given title and without sections).
   */
  readonly #topicManuals = resource({
    params: () => (this.current() === null ? { base: this.basePath(), topics: this.topics() } : undefined),
    loader: ({ params }) =>
      Promise.all(
        params.topics.map((topic) =>
          this.#load(`${params.base}/${topic.article}`).then(
            (markdown) => ({ title: helpTitle(markdown), sections: helpSections(markdown) }),
            () => ({ title: null, sections: [] as HelpSection[] }),
          ),
        ),
      ),
  });
  protected readonly topicsWithSections = computed(() => {
    const manuals = this.#topicManuals.hasValue() ? this.#topicManuals.value() : [];
    return this.topics().map((topic, index) => ({
      ...topic,
      title: manuals[index]?.title || topic.title,
      sections: manuals[index]?.sections ?? [],
    }));
  });
  protected readonly isLoading = this.#markdown.isLoading;
  protected readonly hasError = computed(() => !!this.#markdown.error());

  readonly #tocLabel = toSignal(inject(TranslateService).stream('HELP_IN_THIS_ARTICLE'), { initialValue: '' });
  #sanitizer = inject(DomSanitizer);
  // Our own manuals, from our own server: trusted, and the anchors need their ids (which Angular would strip)
  protected readonly html = computed(() => {
    const markdown = this.#markdown.hasValue() ? this.#markdown.value() : undefined;
    return markdown === undefined
      ? null
      : this.#sanitizer.bypassSecurityTrustHtml(renderHelp(markdown, this.basePath(), capitalize(this.#tocLabel())));
  });

  private readonly body = viewChild.required<ElementRef<HTMLElement>>('body');
  #injector = inject(Injector);
  #host = inject<ElementRef<HTMLElement>>(ElementRef);
  #document = inject(DOCUMENT);

  constructor() {
    // Scroll to the anchor (or the top) once the manual shows
    effect(() => {
      const anchor = this.current()?.split('#')[1];
      if (!this.html()) return;
      afterNextRender(
        () => {
          const body = this.body().nativeElement;
          const target = anchor ? body.querySelector(`[id="${CSS.escape(decodeURIComponent(anchor))}"]`) : null;
          if (target) target.scrollIntoView({ block: 'start' });
          else body.scrollTop = 0;
        },
        { injector: this.#injector },
      );
    });
  }

  /**
   * Escape also closes the help when the focus is in the app, but not while a dialog, a popup or the search is open:
   * then the escape is theirs (it closes them first). In the panel itself, escape always closes it (see the host).
   */
  onDocumentEscape(event: Event) {
    const target = event.target;
    if (event.defaultPrevented) return;
    if (target instanceof Node && this.#host.nativeElement.contains(target)) return;
    if (target instanceof Element && target.closest('rc-search-box')) return;
    if (this.#document.querySelector(ESCAPE_OWNERS)) return;
    this.closed.emit();
  }

  open(article: string | null) {
    this.history.update((history) => [...history, this.current()]);
    this.current.set(article);
  }

  back() {
    const history = this.history();
    if (!history.length) return;
    this.current.set(history[history.length - 1]);
    this.history.set(history.slice(0, -1));
  }

  /** Links between the manuals open here; other links and the screenshots (full size) in a new tab. */
  onArticleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target instanceof HTMLImageElement) {
      window.open(target.src, '_blank', 'noopener');
      return;
    }
    const href = target.closest('a')?.getAttribute('href');
    if (!href) return;
    event.preventDefault();
    if (/^([a-z][a-z0-9+.-]*:|\/)/i.test(href)) {
      window.open(href, '_blank', 'noopener');
    } else {
      this.open(href.startsWith('#') ? `${this.#file()}${href}` : href);
    }
  }
}
